import { Link } from 'react-router-dom';
import {
  IMP_MDDM_ID,
  PLANNING_TEAM,
  EXTENDED_PLANNING,
  IMPLEMENTATION_LAYERS,
  LAYER_ZERO,
  LAYER_ONE,
  LAYER_TWO,
  LAYER_THREE,
  LAYER_FOUR,
  LAYER_FIVE,
  MODULE_DEPENDENCY_FIELDS,
  DATABASE_DEPENDENCIES,
  API_DEPENDENCIES,
  EVENT_DEPENDENCIES,
  AI_DEPENDENCIES,
  FRONTEND_DEPENDENCIES,
  BACKEND_DEPENDENCIES,
  WORKFLOW_DEPENDENCIES,
  PARALLEL_DEVELOPMENT,
  CRITICAL_PATH,
  EXECUTION_PRIORITIES,
  ESTIMATION,
  TRACEABILITY,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_TEAM,
  DEMO_LAYERS,
  DEMO_MODULES,
  DEMO_DATA_API_EVENT,
  DEMO_AI,
  DEMO_FRONTEND_BACKEND,
  DEMO_PARALLEL_CRITICAL,
  DEMO_EXECUTIVE,
  buildDependencyDashboard,
} from '../../masterdependency/framework';
import {
  DependencyTeamPanel,
  ImplementationLayersPanel,
  ModuleDependencyPanel,
  DataAPIEventPanel,
  AIDependencyPanel,
  FrontendBackendWorkflowPanel,
  ParallelCriticalPathPanel,
  ExecutiveDependencyDashboardPanel,
} from '../../components/masterdependency';
import { Card, KpiCard } from '../../components/ui';

export function MasterDependencyMatrixPage() {
  const dashboard = buildDependencyDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{IMP_MDDM_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Master Development Dependency Matrix</h1>
        <p className="mt-1 text-sm text-slate-500">
          Authoritative dependency reference — build order, integration sequence, critical path, and parallel development
          opportunities across all modules, APIs, events, and AI capabilities.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Planning Roles" value={dashboard.planning_team_roles.toString()} />
        <KpiCard label="Layers" value={dashboard.implementation_layers.toString()} />
        <KpiCard label="Module Fields" value={dashboard.module_dependencies.toString()} />
        <KpiCard label="Priority Levels" value={dashboard.priority_levels.toString()} />
        <KpiCard label="Governance Areas" value={dashboard.governance_areas.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Dependency Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Dependency Planning Team</h2>
        <p className="mt-1 text-sm text-slate-500">{PLANNING_TEAM.join(' · ')} · {EXTENDED_PLANNING.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DependencyTeamPanel members={DEMO_TEAM} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Implementation Layers (0–5)</h2>
        <p className="mt-1 text-xs text-slate-400">
          L0: {LAYER_ZERO.slice(0, 4).join(', ')}… · L1: {LAYER_ONE.slice(0, 4).join(', ')}… · L2: {LAYER_TWO.slice(0, 4).join(', ')}…
        </p>
        <p className="mt-1 text-xs text-slate-400">
          L3: {LAYER_THREE.join(', ')} · L4: {LAYER_FOUR.slice(0, 4).join(', ')}… · L5: {LAYER_FIVE.slice(0, 4).join(', ')}…
        </p>
        <Card className="mt-4" padding="sm">
          <ImplementationLayersPanel items={DEMO_LAYERS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Module Dependency Matrix</h2>
        <p className="mt-1 text-sm text-slate-500">{MODULE_DEPENDENCY_FIELDS.slice(0, 7).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{EXECUTION_PRIORITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ModuleDependencyPanel items={DEMO_MODULES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Database, API & Event Dependencies</h2>
        <p className="mt-1 text-sm text-slate-500">{DATABASE_DEPENDENCIES.slice(0, 5).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{API_DEPENDENCIES.slice(0, 5).join(' · ')}… · {EVENT_DEPENDENCIES.slice(0, 5).join(' · ')}…</p>
        <Card className="mt-4" padding="sm">
          <DataAPIEventPanel items={DEMO_DATA_API_EVENT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Dependency Matrix</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_DEPENDENCIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIDependencyPanel items={DEMO_AI} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Frontend, Backend & Workflow Dependencies</h2>
        <p className="mt-1 text-sm text-slate-500">{FRONTEND_DEPENDENCIES.slice(0, 5).join(' · ')}… · {BACKEND_DEPENDENCIES.slice(0, 5).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{WORKFLOW_DEPENDENCIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <FrontendBackendWorkflowPanel items={DEMO_FRONTEND_BACKEND} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Parallel Development & Critical Path</h2>
        <p className="mt-1 text-sm text-slate-500">{PARALLEL_DEVELOPMENT.join(' · ')} · {CRITICAL_PATH.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{ESTIMATION.join(' · ')} · {TRACEABILITY.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ParallelCriticalPathPanel items={DEMO_PARALLEL_CRITICAL} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive Dependency Dashboard</h2>
        <p className="mt-1 text-sm text-slate-500">{IMPLEMENTATION_LAYERS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ExecutiveDependencyDashboardPanel items={DEMO_EXECUTIVE} />
        </Card>
      </section>
    </div>
  );
}
